import {
    Smile,
    BellOff,
    Settings,
    Moon,
    LogOut,
    ChevronRight,
} from 'lucide-react';
import { Avatar } from './ui/Avatar';
import { useUser } from '../contexts/UserContext';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface UserMenuProps {
    isOpen: boolean;
    onClose: () => void;
}

export function UserMenu({ isOpen, onClose }: UserMenuProps) {
    const { user, logout, openSettings, toggleTheme, theme } = useUser();
    const [isMobile, setIsMobile] = useState(false);

    // Detect mobile/tablet
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    if (!user) return null;

    // Mobile Layout (Bottom Sheet)
    if (isMobile) {
        return (
            <AnimatePresence>
                {isOpen && (
                    <>
                        {/* Overlay */}
                        <motion.div
                            {...({
                                initial: { opacity: 0 },
                                animate: { opacity: 1 },
                                exit: { opacity: 0 }
                            } as any)}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-50"
                            onClick={onClose}
                        />

                        {/* Bottom Sheet */}
                        <motion.div
                            {...({
                                initial: { y: "100%" },
                                animate: { y: 0 },
                                exit: { y: "100%" },
                                drag: "y",
                                dragConstraints: { top: 0 },
                                dragElastic: { top: 0, bottom: 0.2 }
                            } as any)}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            onDragEnd={(_event: any, info: any) => {
                                if (info.offset.y > 100) {
                                    onClose()
                                }
                            }}
                            className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-black rounded-t-[24px] overflow-hidden max-h-[85vh] flex flex-col shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.3)]"
                            role="dialog"
                            aria-modal="true"
                        >
                            {/* Drag Handle Area (Non-scrollable) */}
                            <div className="w-full flex justify-center pt-3 pb-4 shrink-0 cursor-grab active:cursor-grabbing">
                                <div className="w-12 h-1.5 bg-gray-300 dark:bg-zinc-700 rounded-full" />
                            </div>

                            {/* Scrollable Content */}
                            <div className="flex-1 overflow-y-auto overflow-x-hidden pb-8">
                                {/* User Header Card */}
                                <div className="px-4 pt-2 pb-6">
                                    <div className="w-full bg-gray-50 dark:bg-zinc-900 rounded-2xl p-4 flex items-center justify-between">
                                        <div className="flex items-center gap-4 flex-1">
                                            <Avatar size="md" alt={user.name} src={user.avatar} />
                                            <div className="flex flex-col flex-1 min-w-0">
                                                <span className="text-base font-semibold text-gray-900 dark:text-white leading-tight truncate">
                                                    {user.name}
                                                </span>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <div className={`w-2 h-2 rounded-full ${user.status === 'online' ? 'bg-green-500' : 'bg-gray-400'}`} />
                                                    <span className="text-xs text-gray-500 dark:text-zinc-400 font-normal capitalize truncate">
                                                        {user.status}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <button className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-3 py-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ml-2">
                                            Perfil
                                        </button>
                                    </div>
                                </div>

                                {/* Account Section */}
                                <div className="px-4 mb-6">
                                    <p className="px-2 text-xs font-normal text-gray-500 dark:text-zinc-500 mb-2">Conta</p>
                                    <div className="bg-gray-50 dark:bg-zinc-900/50 rounded-2xl overflow-hidden">
                                        <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-100 dark:hover:bg-zinc-800/50 transition-colors text-left">
                                            <div className="flex items-center gap-4">
                                                <Smile className="w-6 h-6 shrink-0 text-gray-700 dark:text-zinc-400" />
                                                <div className="flex flex-col">
                                                    <span className="text-[15px] font-semibold text-gray-900 dark:text-white">Configurar status</span>
                                                    <span className="text-xs text-gray-500 dark:text-zinc-500 font-normal mt-0.5">Personalize sua presença</span>
                                                </div>
                                            </div>
                                        </button>

                                        <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-100 dark:hover:bg-zinc-800/50 transition-colors text-left">
                                            <div className="flex items-center gap-4">
                                                <BellOff className="w-6 h-6 shrink-0 text-gray-700 dark:text-zinc-400" />
                                                <div className="flex flex-col">
                                                    <span className="text-[15px] font-semibold text-gray-900 dark:text-white">Silenciar notificações</span>
                                                    <span className="text-xs text-gray-500 dark:text-zinc-500 font-normal mt-0.5">Controle de alertas</span>
                                                </div>
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-gray-400 dark:text-zinc-600" />
                                        </button>
                                    </div>
                                </div>

                                {/* Settings Section */}
                                <div className="px-4 mb-6">
                                    <p className="px-2 text-xs font-normal text-gray-500 dark:text-zinc-500 mb-2">Preferências</p>
                                    <div className="bg-gray-50 dark:bg-zinc-900/50 rounded-2xl overflow-hidden">
                                        <button
                                            onClick={() => {
                                                openSettings();
                                                onClose();
                                            }}
                                            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-100 dark:hover:bg-zinc-800/50 transition-colors text-left"
                                        >
                                            <div className="flex items-center gap-4">
                                                <Settings className="w-6 h-6 shrink-0 text-gray-700 dark:text-zinc-400" />
                                                <div className="flex flex-col">
                                                    <span className="text-[15px] font-semibold text-gray-900 dark:text-white">Configurações</span>
                                                    <span className="text-xs text-gray-500 dark:text-zinc-500 font-normal mt-0.5">Ajustes de IA e preferências</span>
                                                </div>
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-gray-400 dark:text-zinc-600" />
                                        </button>

                                        {/* Dark Mode Toggle */}
                                        <button
                                            onClick={toggleTheme}
                                            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-100 dark:hover:bg-zinc-800/50 transition-colors text-left"
                                        >
                                            <div className="flex items-center gap-4">
                                                <Moon className="w-6 h-6 shrink-0 text-gray-700 dark:text-zinc-400" />
                                                <div className="flex flex-col">
                                                    <span className="text-[15px] font-semibold text-gray-900 dark:text-white">Dark Mode</span>
                                                    <span className="text-xs text-gray-500 dark:text-zinc-500 font-normal mt-0.5">Tema escuro ativado</span>
                                                </div>
                                            </div>
                                            <div className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${theme === 'dark' ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-700'}`}>
                                                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${theme === 'dark' ? 'translate-x-6' : 'translate-x-1'}`} />
                                            </div>
                                        </button>
                                    </div>
                                </div>

                                {/* Logout Button */}
                                <div className="px-4 mb-4">
                                    <button
                                        onClick={() => {
                                            logout();
                                            onClose();
                                        }}
                                        className="w-full bg-gray-50 dark:bg-zinc-900/50 rounded-2xl px-4 py-4 flex items-center gap-4 hover:bg-gray-100 dark:hover:bg-zinc-800/50 transition-colors group"
                                    >
                                        <LogOut className="w-6 h-6 text-red-500" />
                                        <span className="text-[15px] font-semibold text-red-500">Sair da conta</span>
                                    </button>
                                </div>

                                {/* Safe Area Spacer for iOS Home Bar */}
                                <div className="h-8" />
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        );
    }

    // Desktop Layout (Dropdown)
    return (
        <>
            {isOpen && (
                <div
                    className="absolute top-12 right-0 w-80 bg-white dark:bg-black rounded-xl shadow-xl border border-gray-200 dark:border-gray-800 z-50 overflow-hidden font-sans"
                    role="dialog"
                    aria-modal="true"
                >
                    {/* User Header Card */}
                    <div className="p-4">
                        <div className="bg-gray-50 dark:bg-zinc-900 rounded-xl p-3 flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <Avatar size="md" alt={user.name} src={user.avatar} />
                                <div className="flex flex-col flex-1 min-w-0">
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white leading-tight truncate">
                                        {user.name}
                                    </span>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                        <div className={`w-2 h-2 rounded-full ${user.status === 'online' ? 'bg-green-500' : 'bg-gray-400'}`} />
                                        <span className="text-xs text-gray-500 dark:text-zinc-400 capitalize truncate">
                                            {user.status}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <button className="text-[10px] font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2.5 py-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ml-2 shrink-0">
                                Perfil
                            </button>
                        </div>
                    </div>

                    {/* Account Section */}
                    <div className="px-4 pb-3">
                        <p className="px-2 text-[10px] font-medium text-gray-500 dark:text-zinc-500 mb-2 uppercase tracking-wider">Conta</p>
                        <div className="bg-gray-50 dark:bg-zinc-900/50 rounded-xl overflow-hidden">
                            <button className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-zinc-800/50 transition-colors text-left">
                                <Smile className="w-4 h-4 shrink-0 text-gray-600 dark:text-zinc-400" />
                                <span className="text-sm font-medium text-gray-900 dark:text-white">Configurar status</span>
                            </button>
                            <button className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-zinc-800/50 transition-colors text-left group">
                                <div className="flex items-center gap-3">
                                    <BellOff className="w-4 h-4 shrink-0 text-gray-600 dark:text-zinc-400" />
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">Silenciar notificações</span>
                                </div>
                                <ChevronRight className="w-3.5 h-3.5 text-gray-400 dark:text-zinc-600" />
                            </button>
                        </div>
                    </div>

                    {/* Settings Section */}
                    <div className="px-4 pb-3">
                        <p className="px-2 text-[10px] font-medium text-gray-500 dark:text-zinc-500 mb-2 uppercase tracking-wider">Preferências</p>
                        <div className="bg-gray-50 dark:bg-zinc-900/50 rounded-xl overflow-hidden">
                            <button
                                onClick={() => {
                                    openSettings();
                                    onClose();
                                }}
                                className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-zinc-800/50 transition-colors text-left"
                            >
                                <div className="flex items-center gap-3">
                                    <Settings className="w-4 h-4 shrink-0 text-gray-600 dark:text-zinc-400" />
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">Configurações</span>
                                </div>
                                <ChevronRight className="w-3.5 h-3.5 text-gray-400 dark:text-zinc-600" />
                            </button>

                            {/* Dark Mode Toggle */}
                            <button
                                onClick={toggleTheme}
                                className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-zinc-800/50 transition-colors text-left"
                            >
                                <div className="flex items-center gap-3">
                                    <Moon className="w-4 h-4 shrink-0 text-gray-600 dark:text-zinc-400" />
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">Dark Mode</span>
                                </div>
                                <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${theme === 'dark' ? 'bg-blue-600' : 'bg-gray-300'}`}>
                                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${theme === 'dark' ? 'translate-x-5' : 'translate-x-0.5'}`} />
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Logout Button */}
                    <div className="p-4 pt-2">
                        <button
                            onClick={() => {
                                logout();
                                onClose();
                            }}
                            className="w-full bg-gray-50 dark:bg-zinc-900/50 rounded-xl px-3 py-2.5 flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-zinc-800/50 transition-colors"
                        >
                            <LogOut className="w-4 h-4 text-red-500" />
                            <span className="text-sm font-medium text-red-500">Sair da conta</span>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
